import { isValidHarnessSquad, type HarnessSquad } from './squad';

/**
 * Query parameter carrying a shared squad.
 *
 * Sharing is the point: a simulation is watched on a deployed staging build,
 * usually by more than one person, and re-picking fifteen players on each
 * machine to look at the same run is not a workable way to do that.
 */
export const SQUAD_QUERY_PARAM = 'simulatedSquad';

const STORAGE_KEY = 'ffh:live-simulation-squad';
const ENCODING_VERSION = 1;

type EncodedEnvelope = {
  version: number;
  squad: HarnessSquad;
};

/**
 * URL-safe base64 over UTF-8 bytes.
 *
 * `btoa` alone throws on anything outside Latin-1, and real player names carry
 * accents and diacritics, so the string has to be encoded to bytes first.
 *
 * The result runs to roughly 2KB for a full squad. That is long for a URL but
 * well inside what every current browser accepts, and the alternative, a
 * bespoke positional format, buys a shorter link at the cost of an encoding
 * that has to be kept in step by hand.
 */
const toBase64Url = (value: string): string => {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const fromBase64Url = (value: string): string => {
  const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

export const encodeHarnessSquad = (squad: HarnessSquad): string =>
  toBase64Url(
    JSON.stringify({
      version: ENCODING_VERSION,
      squad
    } satisfies EncodedEnvelope)
  );

const FPL_POSITIONS: readonly string[] = ['GK', 'DEF', 'MID', 'FWD'];
const ACTIVE_CHIPS: readonly unknown[] = [
  null,
  'bboost',
  '3xc',
  'wildcard',
  'freehit'
];

const isPickShape = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null) return false;
  const pick = value as Record<string, unknown>;
  return (
    typeof pick.optaId === 'string' &&
    pick.optaId.length > 0 &&
    typeof pick.fplId === 'number' &&
    Number.isFinite(pick.fplId) &&
    typeof pick.displayName === 'string' &&
    typeof pick.teamShortName === 'string' &&
    typeof pick.slot === 'number' &&
    Number.isInteger(pick.slot) &&
    FPL_POSITIONS.includes(pick.position as string) &&
    Array.isArray(pick.optaMatchIds) &&
    pick.optaMatchIds.every((matchId) => typeof matchId === 'string')
  );
};

/**
 * Whether the decoded value is structurally a squad.
 *
 * Separate from `validateHarnessSquad`, which answers a different question:
 * that one asks whether a well-formed squad is a legal FPL one, and to do that
 * it already assumes the fields are the types it reads. A payload can satisfy
 * every rule it checks while carrying, say, no `optaMatchIds` at all, and
 * would then throw in `collectUniqueMatchIds` long after the point where it
 * could have been refused.
 */
const isHarnessSquadShape = (value: unknown): value is HarnessSquad => {
  if (typeof value !== 'object' || value === null) return false;
  const squad = value as Record<string, unknown>;
  return (
    typeof squad.gameweek === 'number' &&
    typeof squad.captainSlot === 'number' &&
    typeof squad.viceCaptainSlot === 'number' &&
    ACTIVE_CHIPS.includes(squad.activeChip) &&
    Array.isArray(squad.picks) &&
    squad.picks.every(isPickShape)
  );
};

/**
 * Decode a shared or stored squad, or null if it cannot be trusted.
 *
 * Everything is rejected rather than repaired: a squad arrives from a URL
 * somebody pasted or from storage written by an older build, and a
 * half-understood one would produce a pitch that looks deliberate while
 * behaving arbitrarily. Both shape and legality are checked here, so neither a
 * malformed payload nor an illegal squad can reach the live stack by way of a
 * stale link.
 */
export const decodeHarnessSquad = (encoded: string): HarnessSquad | null => {
  try {
    const envelope = JSON.parse(fromBase64Url(encoded)) as EncodedEnvelope;
    if (envelope?.version !== ENCODING_VERSION) return null;
    if (!isHarnessSquadShape(envelope.squad)) return null;
    return isValidHarnessSquad(envelope.squad) ? envelope.squad : null;
  } catch {
    return null;
  }
};

/**
 * A simulation runs for a wall-clock period, and reconnection and gap-fill are
 * paths this whole epic exists to exercise, so a refresh mid-run has to keep
 * the squad rather than drop the operator back at an empty picker.
 */
export const readStoredHarnessSquad = (): HarnessSquad | null => {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === null ? null : decodeHarnessSquad(stored);
};

export const writeStoredHarnessSquad = (squad: HarnessSquad | null): void => {
  if (typeof window === 'undefined') return;
  if (squad === null) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, encodeHarnessSquad(squad));
};
